// src/services/auth/authInvite.ts
import { supabase } from '@/lib/supabase';
import { checkSubscriptionStatusService } from './authSession';
import * as Sentry from '@sentry/react';

type InviteDeps = {
    setIsAuthenticated: (v: boolean) => void;
    setLoading: (v: boolean) => void;
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setSubscriptionLoading: (v: boolean) => void;
};

type OnboardingDeps = {
    user: any | null;
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setLoading: (v: boolean) => void;
    setSubscriptionLoading: (v: boolean) => void;
};

type AuthResult = { success: boolean; error: string; redirectTo?: string };

export const onboardInviteEdge = async ({
    token,
    user_id,
    email,
    first_name,
    last_name,
    setUser,
    setOrganization,
    setIsAuthenticated,
    setIsPaid,
    setSubscriptionPlan,
    setSubscriptionLoading,
}: {
    token: string;
    user_id: string;
    email: string;
    first_name: string;
    last_name: string;
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setSubscriptionLoading: (v: boolean) => void;
}) => {

    const { data: rpcResult, error: rpcErr } = await supabase.rpc(
        'invite_onboard_transaction',
        {
            p_user_id: user_id,
            p_token: token,
            p_email: email,
            p_first_name: first_name,
            p_last_name: last_name
        },
    );

    if (rpcErr) {
        const code = (rpcErr as any)?.code;
        const msg = (rpcErr as any)?.message;

        switch (code) {
            case 'P0001':
                throw new Error("Invito non valido, scaduto o già utilizzato.");
            case 'P0002':
                throw new Error("Hai raggiunto il numero massimo di utenti attivi per il piano attuale.");
            case 'P0003':
                throw new Error("Il negozio non ha orari di lavoro configurati. Contatta l'amministratore.");
            case 'P0004':
                throw new Error("Questo utente è già assegnato a un altro negozio.");
            default:
                Sentry.captureException(rpcErr, {
                    tags: { feature: 'invite', action: 'onboard_invite_rpc_error' },
                    extra: { user_id, email, token }
                });
                throw new Error(msg || "Errore sconosciuto durante l'elaborazione dell'invito.");
        }
    }

    const { hairdresser, shop } = (rpcResult as any) || {};
    if (!hairdresser || !shop) {
        const err = new Error("Errore durante l'autenticazione. Riprova.");
        Sentry.captureException(err, {
            tags: { feature: 'invite', action: 'onboard_invite_missing_data' },
            extra: { user_id, email, rpcResult }
        });
        throw err;
    }

    setUser(hairdresser);
    setOrganization(shop);
    setIsAuthenticated(true);

    await checkSubscriptionStatusService(shop.id, {
        setIsPaid,
        setSubscriptionPlan,
        setSubscriptionLoading,
    });
};

export const registerWithTokenService = async (
    userData: any,
    token: string,
    {
        setIsAuthenticated,
        setLoading
    }: InviteDeps,
): Promise<AuthResult> => {
    try {
        setLoading(true);

        // 1. TENTATIVO SIGN UP
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback?redirect_type=invite`,
                data: {
                    full_name: `${userData.first_name} ${userData.last_name}`,
                    email: userData.email,
                    accepted_terms: !!userData.accepted_terms,
                    consent_marketing: !!userData.consent_marketing,
                    privacy_policy_read: !!userData.privacy_policy_read
                },
            },
        });

        // GESTIONE ERRORE ESPLICITO
        if (authError) {
            const msg = authError.message.toLowerCase();
            const code = (authError as any).code;

            // Se l'utente esiste già (ma il login sopra è fallito, quindi password diversa o OAuth)
            if (code === 'user_already_exists' || msg.includes('already registered')) {
                return {
                    success: false,
                    error: 'USER_EXISTS_LOGIN_REQUIRED' // Codice speciale per la UI
                };
            }

            // Altri errori reali
            let errorMessage = 'Errore durante la registrazione';
            if (msg.includes('rate limit')) errorMessage = 'Troppi tentativi. Riprova più tardi.';
            throw new Error(errorMessage);
        }

        // GESTIONE ERRORE IMPLICITO
        if (authData.user && authData.user.identities && authData.user.identities.length === 0) {
            console.warn("Rilevato utente esistente su invito (identities vuoto).");
            return { success: false, error: 'USER_EXISTS_LOGIN_REQUIRED' };
        }

        if (!authData?.user) throw new Error('Errore durante la registrazione');

        // 3. REGISTRAZIONE OK
        localStorage.setItem('clip_invite_token', token);
        localStorage.setItem('clip_pending_email', userData.email);
        setIsAuthenticated(false);
        return { success: true, error: '' };

    } catch (error: any) {
        Sentry.captureException(error, {
            tags: { feature: 'invite', action: 'register_with_token' },
            extra: { email: userData?.email }
        });
        return {
            success: false,
            error: error?.message || 'Errore durante la registrazione con invito',
        };
    } finally {
        setLoading(false);
    }
};

export const resendConfirmationEmailService = async (): Promise<{
    success: boolean;
    error: string;
}> => {
    try {
        const email = localStorage.getItem('clip_pending_email') || '';
        if (!email) {
            throw new Error(
                'Indirizzo email non disponibile. Torna al login e riprova.',
            );
        }

        const { error } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: {
                emailRedirectTo: `${window.location.origin}/auth/callback?redirect_type=invite`,
            },
        });

        if (error) {
            console.error(
                'Errore nel reinvio della mail di conferma:',
                error,
            );
            throw new Error('Errore nel reinvio della mail di conferma');
        }

        return { success: true, error: '' };
    } catch (e: any) {
        console.error(
            'Errore inatteso nel reinvio della conferma email:',
            e,
        );
        return {
            success: false,
            error:
                e?.message ||
                "Impossibile reinviare l'email in questo momento. Attendi e riprova.",
        };
    }
};

export const completeOnboardingAfterConfirmationService = async ({
    user,
    setUser,
    setOrganization,
    setIsAuthenticated,
    setIsPaid,
    setSubscriptionPlan,
    setLoading,
    setSubscriptionLoading,
}: OnboardingDeps): Promise<{ success: boolean; error: string }> => {
    try {
        setLoading(true);

        const token = localStorage.getItem('clip_invite_token') || '';

        if (!token || !user) {
            throw new Error('Dati mancanti. Ripeti il processo di invito.');
        }

        if (!user?.user_metadata) {
            throw new Error(
                'Errore di sincronizzazione. Ripeti il processo di invito.',
            );
        }

        const userMetadata = user.user_metadata || {};
        const fullName = userMetadata.full_name || '';

        // Fallback per nome/cognome
        let firstName = '';
        let lastName = '';

        if (fullName) {
            const parts = fullName.split(' ');
            firstName = parts[0];
            lastName = parts.slice(1).join(' ');
        }

        if (!firstName || !lastName) {
            throw new Error('Dati mancanti. Ripeti il processo di invito.');
        }

        await onboardInviteEdge({
            token,
            user_id: user.id,
            email: user.email,
            first_name: firstName,
            last_name: lastName,
            setUser,
            setOrganization,
            setIsAuthenticated,
            setIsPaid,
            setSubscriptionPlan,
            setSubscriptionLoading,
        });

        localStorage.removeItem('clip_invite_token');
        localStorage.removeItem('clip_pending_email');
        return { success: true, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'invite', action: 'complete_onboarding' },
            extra: { userId: user?.id }
        });
        return {
            success: false,
            error:
                e?.message || "Errore inatteso durante l'autenticazione.",
        };
    } finally {
        setLoading(false);
    }
};